import moment from "moment";

// Late-payment policy — client side of ots-server/utils/latePolicy.js. Keep the
// two in step: the server is the authority (it validates on save and computes
// the fee that is actually charged), this file drives the form and lets the
// screens describe/estimate a fee from the same rules.
//
// A property stores a DEFAULT policy; nothing is calculated at registration.
// The fee is only ever worked out later, per bill, from the policy and the
// bill's due date. Late fees are separate from eviction/default handling.

export const LATE_FEE_TYPES = {
  // Kept only so a property/tenancy that already stored one of these still
  // computes/displays correctly. Never assigned again — the admin's global
  // policy (ADMIN_SELECTABLE_TYPES) only ever picks one of the other three.
  PER_DAY_FIXED: "PER_DAY_FIXED",
  PER_DAY_PERCENTAGE: "PER_DAY_PERCENTAGE",
  PER_MONTH_PERCENTAGE: "PER_MONTH_PERCENTAGE",
  FIXED_AFTER_GRACE: "FIXED_AFTER_GRACE",
};

// The three shapes the admin can choose from for the whole platform (see
// SystemSettings.latePaymentPolicy on the server). The owner's property form
// only ever displays whichever one is current — see PropertyFormModal's
// LatePolicyField.
export const ADMIN_SELECTABLE_TYPES = [
  LATE_FEE_TYPES.PER_DAY_PERCENTAGE,
  LATE_FEE_TYPES.PER_MONTH_PERCENTAGE,
  LATE_FEE_TYPES.FIXED_AFTER_GRACE,
];

export const LATE_FEE_TYPE_OPTIONS = [
  { value: LATE_FEE_TYPES.PER_DAY_FIXED, label: "Per Day - Fixed Amount" },
  { value: LATE_FEE_TYPES.PER_DAY_PERCENTAGE, label: "Per Day - Percentage" },
  { value: LATE_FEE_TYPES.PER_MONTH_PERCENTAGE, label: "Per Month - Percentage" },
  { value: LATE_FEE_TYPES.FIXED_AFTER_GRACE, label: "Fixed Amount After Grace Period" },
];

// Technical sanity limits (mirrors the server), not legal ones.
export const LATE_FEE_LIMITS = {
  MAX_GRACE_DAYS: 30,
  MAX_DAILY_PERCENTAGE: 1, // % of monthly rent, per day
  MAX_MONTHLY_PERCENTAGE: 10, // % of monthly rent, per month
  MAX_CAP_PERCENTAGE: 25, // % of monthly rent, total late fee
  MAX_FIXED_AMOUNT: 20000, // Rs, one-time — a global flat number, kept modest
};

export const DEFAULT_GRACE_DAYS = 5;

// Reasonable starting values for each fee type, so switching type never leaves
// a field holding a number that means something else (100 Rs vs 100 %).
export const LATE_FEE_TYPE_DEFAULTS = {
  [LATE_FEE_TYPES.PER_DAY_FIXED]: { amount: "100", maximumAmount: "2000", maximumPercentage: "" },
  [LATE_FEE_TYPES.PER_DAY_PERCENTAGE]: { amount: "0.10", maximumAmount: "", maximumPercentage: "10" },
  [LATE_FEE_TYPES.PER_MONTH_PERCENTAGE]: { amount: "5", maximumAmount: "", maximumPercentage: "10" },
  [LATE_FEE_TYPES.FIXED_AFTER_GRACE]: { amount: "500", maximumAmount: "", maximumPercentage: "" },
};

const toNumber = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const round2 = (n) => Math.round(n * 100) / 100;

// Values Formik holds for a property with no late fee.
export const emptyLatePolicyForm = () => ({
  enabled: false,
  type: LATE_FEE_TYPES.PER_DAY_FIXED,
  gracePeriodDays: String(DEFAULT_GRACE_DAYS),
  ...LATE_FEE_TYPE_DEFAULTS[LATE_FEE_TYPES.PER_DAY_FIXED],
});

/**
 * The policy in force for a flat (or anything shaped like one). Properties
 * saved before policies existed only carry `lateChargesPerDay` +
 * `rentRelaxationDays`; those read as a PER_DAY_FIXED policy with no cap.
 */
export function resolveLatePolicy(source = {}) {
  let stored = source?.latePaymentPolicy;
  if (typeof stored === "string") {
    try {
      stored = JSON.parse(stored);
    } catch (e) {
      stored = null;
    }
  }

  if (stored && typeof stored.enabled === "boolean") {
    return {
      enabled: stored.enabled,
      type: stored.type || LATE_FEE_TYPES.PER_DAY_FIXED,
      amount: Number(stored.amount) || 0,
      gracePeriodDays: Number(stored.gracePeriodDays) || 0,
      maximumAmount: toNumber(stored.maximumAmount),
      maximumPercentage: toNumber(stored.maximumPercentage),
    };
  }

  const rate = Number(source?.lateChargesPerDay) || 0;
  const relaxation = toNumber(source?.rentRelaxationDays);
  return {
    enabled: rate > 0,
    type: LATE_FEE_TYPES.PER_DAY_FIXED,
    amount: rate,
    gracePeriodDays: relaxation === null ? DEFAULT_GRACE_DAYS : relaxation,
    maximumAmount: null,
    maximumPercentage: null,
  };
}

/** Stored flat -> Formik values (all strings, as the inputs hold them). */
export function latePolicyToForm(flat) {
  const policy = resolveLatePolicy(flat);
  const defaults = emptyLatePolicyForm();
  const str = (v, fallback) => (v === null || v === undefined ? fallback : String(v));

  if (!policy.enabled) {
    return { ...defaults, gracePeriodDays: str(policy.gracePeriodDays, defaults.gracePeriodDays) };
  }
  return {
    enabled: true,
    type: policy.type,
    amount: str(policy.amount, ""),
    gracePeriodDays: str(policy.gracePeriodDays, defaults.gracePeriodDays),
    maximumAmount: str(policy.maximumAmount, ""),
    maximumPercentage: str(policy.maximumPercentage, ""),
  };
}

/**
 * Formik values -> the object sent to the API. Only the fields that belong to
 * the chosen type are sent, and every number is a real number — a stray "" or
 * NaN can never reach the server.
 */
export function latePolicyFromForm(form = {}) {
  if (!form.enabled) {
    const grace = toNumber(form.gracePeriodDays);
    return {
      enabled: false,
      type: LATE_FEE_TYPES.PER_DAY_FIXED,
      amount: 0,
      gracePeriodDays: Number.isInteger(grace) && grace >= 0 ? grace : DEFAULT_GRACE_DAYS,
      maximumAmount: null,
      maximumPercentage: null,
    };
  }
  return {
    enabled: true,
    type: form.type,
    amount: toNumber(form.amount),
    gracePeriodDays: toNumber(form.gracePeriodDays),
    maximumAmount: form.type === LATE_FEE_TYPES.PER_DAY_FIXED ? toNumber(form.maximumAmount) : null,
    maximumPercentage: [
      LATE_FEE_TYPES.PER_DAY_PERCENTAGE,
      LATE_FEE_TYPES.PER_MONTH_PERCENTAGE,
    ].includes(form.type)
      ? toNumber(form.maximumPercentage)
      : null,
  };
}

/**
 * The fee for a payment `daysPastGrace` days beyond the end of the grace
 * period (0 or less -> nothing owed). Same rules as the server.
 */
export function computeLateFee(policy, { rent = 0, daysPastGrace = 0 } = {}) {
  if (!policy || !policy.enabled || !(daysPastGrace > 0)) return 0;
  const amount = Number(policy.amount) || 0;
  if (amount <= 0) return 0;

  switch (policy.type) {
    case LATE_FEE_TYPES.PER_DAY_FIXED: {
      const fee = daysPastGrace * amount;
      const cap = toNumber(policy.maximumAmount);
      return round2(cap !== null && cap > 0 ? Math.min(fee, cap) : fee);
    }
    case LATE_FEE_TYPES.PER_DAY_PERCENTAGE: {
      const monthlyRent = Number(rent) || 0;
      if (monthlyRent <= 0) return 0;
      const fee = daysPastGrace * monthlyRent * (amount / 100);
      const capPct = toNumber(policy.maximumPercentage);
      const cap = capPct !== null && capPct > 0 ? monthlyRent * (capPct / 100) : null;
      return round2(cap !== null ? Math.min(fee, cap) : fee);
    }
    case LATE_FEE_TYPES.PER_MONTH_PERCENTAGE: {
      const monthlyRent = Number(rent) || 0;
      if (monthlyRent <= 0) return 0;
      const monthsLate = Math.ceil(daysPastGrace / 30);
      const fee = monthsLate * monthlyRent * (amount / 100);
      const capPct = toNumber(policy.maximumPercentage);
      const cap = capPct !== null && capPct > 0 ? monthlyRent * (capPct / 100) : null;
      return round2(cap !== null ? Math.min(fee, cap) : fee);
    }
    case LATE_FEE_TYPES.FIXED_AFTER_GRACE:
      return round2(amount);
    default:
      return 0;
  }
}

const rs = (n) => `Rs ${Number(n).toLocaleString("en-US", { maximumFractionDigits: 2 })}`;

/** One line describing a policy; null when no late fee applies. */
export function describeLatePolicy(policy) {
  if (!policy || !policy.enabled) return null;
  const days = policy.gracePeriodDays;
  const grace = `${days} day${days === 1 ? "" : "s"} grace period`;
  switch (policy.type) {
    case LATE_FEE_TYPES.PER_DAY_FIXED: {
      const cap = toNumber(policy.maximumAmount);
      return `${rs(policy.amount)}/day after ${grace}${cap ? `, maximum ${rs(cap)}` : ""}`;
    }
    case LATE_FEE_TYPES.PER_DAY_PERCENTAGE: {
      const cap = toNumber(policy.maximumPercentage);
      return `${policy.amount}% of rent per day after ${grace}${cap ? `, maximum ${cap}% of rent` : ""}`;
    }
    case LATE_FEE_TYPES.PER_MONTH_PERCENTAGE: {
      const cap = toNumber(policy.maximumPercentage);
      return `${policy.amount}% of rent per month after ${grace}${cap ? `, maximum ${cap}% of rent` : ""}`;
    }
    case LATE_FEE_TYPES.FIXED_AFTER_GRACE:
      return `${rs(policy.amount)} one-time fee after ${grace}`;
    default:
      return null;
  }
}

/**
 * Late penalty for one bill. `config` is a flat/lease-shaped object
 * (`latePaymentPolicy` or the legacy fields, plus `flatRent` for percentage
 * policies). Grace extends the due date; the first late day is the day after:
 * due 5th + 5 grace days -> nothing through the 10th, fee from the 11th.
 * Calendar days only, so time of day can't shift the result.
 */
export function calculateLatePenalty(dueDateStr, config = {}, referenceDate = new Date()) {
  const policy = resolveLatePolicy(config);
  if (!dueDateStr || !policy.enabled) return { daysOverdue: 0, penalty: 0 };

  const due = moment(dueDateStr, ["DD-MM-YYYY", "YYYY-MM-DD", moment.ISO_8601], true);
  if (!due.isValid()) return { daysOverdue: 0, penalty: 0 };

  const graceUntil = due.clone().add(policy.gracePeriodDays, "days").startOf("day");
  const daysOverdue = moment(referenceDate).startOf("day").diff(graceUntil, "days");
  if (daysOverdue <= 0) return { daysOverdue: 0, penalty: 0 };

  return {
    daysOverdue,
    penalty: computeLateFee(policy, { rent: config.flatRent, daysPastGrace: daysOverdue }),
  };
}
